import panelTexture from '../assets/log_background.png';

// Resolve a stored theme preference to the data-theme attribute value the
// stylesheet uses. 'auto' looks at prefers-color-scheme so the app follows
// the OS appearance toggle without explicit user action.
export function resolveTheme(theme) {
    if (theme === 'auto') {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'dark';
    }
    return theme === 'light' ? 'light' : 'dark';
}

// The dark panel texture must be injected from JS, not declared in
// _theme.scss: a relative url() inside a CSS custom property resolves
// against the DOCUMENT, not the stylesheet that declared it, so in the
// packaged app (file://…/build/index.html) the built ../../static/media
// path pointed two levels above index.html and the image silently failed —
// black panels in production, correct texture in dev (where the dev-server
// URL happens to be absolute). Resolving the webpack asset URL against
// document.baseURI at runtime is correct in dev, packaged, and web builds.
const PANEL_TEXTURE = typeof document !== 'undefined'
    ? `url("${new URL(panelTexture, document.baseURI).href}") center / cover no-repeat`
    : null;

// Single entry point for switching themes: sets data-theme and keeps the
// texture vars in sync (dark gets the texture; light falls back to the
// _theme.scss declarations). All callers must use this instead of setting
// the attribute directly.
export function applyTheme(theme) {
    const resolved = resolveTheme(theme);
    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    // Design v2 paints flat surfaces from its own tokens — never the texture.
    const v2 = root.getAttribute('data-design') === 'v2';
    if (resolved === 'dark' && PANEL_TEXTURE && !v2) {
        root.style.setProperty('--color-panel-bg', PANEL_TEXTURE);
        root.style.setProperty('--color-player-bg', PANEL_TEXTURE);
    } else {
        root.style.removeProperty('--color-panel-bg');
        root.style.removeProperty('--color-player-bg');
    }
    // Design lab (dev only): re-apply the picked surface/accent on top of the
    // theme so a theme switch does not wipe the experiment.
    applyDesignLab(getDesignLab(), { silent: true });
    return resolved;
}

// ---------------------------------------------------------------------------
// Design lab — pre-release A/B of the panel surface + accent palette.
// Dev-only UI (DesignLab.jsx); the values are the 4 surface options that were
// documented as comments in _theme.scss plus 4 accent candidates. Nothing here
// runs in a packaged build unless localStorage 'mixgoat-design-lab' is set.
// ---------------------------------------------------------------------------
export const DESIGN_LAB_KEY = 'mixgoat-design-lab';

export const SURFACES = {
    texture: { label: '1 Texture',  panel: null,                        player: null },
    tint2:   { label: '2 Tint 2%',  panel: 'rgba(255,255,255,0.02)',    player: '#090a0a' },
    flat:    { label: '3 Flat',     panel: '#131313',                   player: '#131313' },
    tint4:   { label: '4 Tint 4%',  panel: 'rgba(255,255,255,0.04)',    player: '#0c0d0d' },
    lifted:  { label: '5 Lifted',   panel: '#1a1c1e',                   player: '#0c0d0d', border: 'rgba(255,255,255,0.06)' },
};

export const ACCENTS = {
    cyan:     { label: 'Cyan',     accent: '#00bcd4', hover: '#00a5bb', light: '#4cc2f1', bright: '#26c6da' },
    teal:     { label: 'Teal',     accent: '#1fd1c0', hover: '#17b8a9', light: '#5fe3d6', bright: '#4de3d4' },
    electric: { label: 'Electric', accent: '#35b8ff', hover: '#1ea6f0', light: '#7bd0ff', bright: '#6cccff' },
    amber:    { label: 'Amber',    accent: '#ffb347', hover: '#f0a030', light: '#ffc46b', bright: '#ffc46b' },
};

// "Looks" — whole-direction presets, not just a surface swap. Each sets the
// page, card, hairline, text and accent together so the app reads as ONE idea.
export const LOOKS = {
    booth: {
        label: 'Booth',
        blurb: 'Warm black, no cards: hairlines and whitespace define regions. Amber accent. Feels like a dim DJ booth.',
        vars: {
            '--color-bg-primary': '#0d0c0b', '--color-bg-secondary': '#0d0c0b', '--color-panel-bg': 'transparent',
            '--color-player-bg': '#080807', '--color-bg-tertiary': '#1c1a17', '--color-bg-input': '#1c1a17', '--color-bg-hover': '#171513',
            '--color-border': 'rgba(255,240,220,0.09)', '--color-border-light': 'rgba(255,240,220,0.14)', '--row-separator': 'rgba(255,240,220,0.08)',
            '--color-text-primary': '#ECE6DC', '--color-text-secondary': '#A69E92', '--color-text-tertiary': '#7A736A',
            '--color-accent': '#ffb347', '--color-accent-hover': '#f0a030', '--color-accent-light': '#ffc46b', '--color-accent-bright': '#ffc46b',
            '--color-loop': '#ffb347', '--color-loop-hover': '#f0a030',
        },
    },
    lit: {
        label: 'Artwork-lit',
        blurb: 'Cards as frosted glass; the page glows with the playing track\'s colour. Apple Music / iOS feel. Every page looks different.',
        ambient: true,
        vars: {
            '--color-bg-primary': '#0f1114', '--color-bg-secondary': 'rgba(255,255,255,0.05)', '--color-panel-bg': 'rgba(255,255,255,0.05)',
            '--color-player-bg': 'rgba(10,12,14,0.75)', '--color-bg-tertiary': 'rgba(255,255,255,0.09)', '--color-bg-input': 'rgba(255,255,255,0.09)', '--color-bg-hover': 'rgba(255,255,255,0.08)',
            '--color-border': 'rgba(255,255,255,0.10)', '--color-border-light': 'rgba(255,255,255,0.16)', '--row-separator': 'rgba(255,255,255,0.08)',
            '--color-accent': '#ffffff', '--color-accent-hover': '#e6e6e6', '--color-accent-light': '#ffffff', '--color-accent-bright': '#ffffff',
            '--color-loop': '#ffffff', '--color-loop-hover': '#e6e6e6',
        },
    },
    signal: {
        label: 'Signal',
        blurb: 'True black OLED page, white type, no cards, ONE neon accent (lime). The waveform is the only colour. Ableton Push / Teenage Engineering.',
        vars: {
            '--color-bg-primary': '#000000', '--color-bg-secondary': '#000000', '--color-panel-bg': 'transparent',
            '--color-player-bg': '#000000', '--color-bg-tertiary': '#161616', '--color-bg-input': '#161616', '--color-bg-hover': '#111111',
            '--color-border': '#222222', '--color-border-light': '#2e2e2e', '--row-separator': '#1a1a1a',
            '--color-text-primary': '#F5F5F5', '--color-text-secondary': '#9A9A9A', '--color-text-tertiary': '#666666',
            '--color-accent': '#c6ff4d', '--color-accent-hover': '#b3f03a', '--color-accent-light': '#d8ff80', '--color-accent-bright': '#d8ff80',
            '--color-loop': '#c6ff4d', '--color-loop-hover': '#b3f03a',
        },
    },
    paper: {
        label: 'Paper',
        blurb: 'Light-first. Warm off-white page, white cards, ink text, one deep accent. Nobody in DJ software does this; daytime prep work reads better on it.',
        theme: 'light',
        vars: {
            '--color-bg-primary': '#F4F1EC', '--color-bg-secondary': '#FFFFFF', '--color-panel-bg': '#FFFFFF',
            '--color-player-bg': '#FFFFFF', '--color-bg-tertiary': '#E9E4DC', '--color-bg-input': '#FFFFFF', '--color-bg-hover': '#EDE8E1',
            '--color-border': '#DDD6CC', '--color-border-light': '#E8E2D9', '--row-separator': 'rgba(0,0,0,0.07)',
            '--color-text-primary': '#1B1A18', '--color-text-secondary': '#5E5A54', '--color-text-tertiary': '#8A857E',
            '--color-accent': '#0E7C86', '--color-accent-hover': '#0B6670', '--color-accent-light': '#139AA6', '--color-accent-bright': '#139AA6',
            '--color-loop': '#0E7C86', '--color-loop-hover': '#0B6670',
        },
    },
};

const ALL_LOOK_VARS = [...new Set(Object.values(LOOKS).flatMap((l) => Object.keys(l.vars)))];

let ambientEl = null;
function setAmbient(on) {
    if (typeof document === 'undefined') return;
    if (!on) { if (ambientEl) ambientEl.remove(); ambientEl = null; return; }
    if (!ambientEl) {
        ambientEl = document.createElement('div');
        ambientEl.id = 'design-lab-ambient';
        Object.assign(ambientEl.style, {
            position: 'fixed', inset: '0', pointerEvents: 'none', zIndex: '99990', mixBlendMode: 'screen', opacity: '0.55',
            background: 'radial-gradient(900px 500px at 28% -5%, rgba(214,120,60,0.55), transparent 60%), radial-gradient(700px 500px at 85% 10%, rgba(60,110,200,0.40), transparent 60%)',
        });
        document.body.appendChild(ambientEl);
    }
}

export function getDesignLab() {
    try {
        return JSON.parse(localStorage.getItem(DESIGN_LAB_KEY) || '{}') || {};
    } catch (e) {
        return {};
    }
}

export function applyDesignLab(lab, { silent } = {}) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    // A "look" overrides everything below it.
    ALL_LOOK_VARS.forEach((v) => root.style.removeProperty(v));
    const look = LOOKS[lab.look];
    if (look) {
        if (look.theme) root.setAttribute('data-theme', look.theme);
        Object.entries(look.vars).forEach(([k, v]) => root.style.setProperty(k, v));
        setAmbient(!!look.ambient);
        if (!silent) { try { localStorage.setItem(DESIGN_LAB_KEY, JSON.stringify(lab)); } catch (e) { /* ignore */ } }
        return;
    }
    setAmbient(false);
    const dark = root.getAttribute('data-theme') !== 'light';
    const surface = SURFACES[lab.surface];
    const accent = ACCENTS[lab.accent];

    if (surface && dark) {
        if (surface.panel) {
            root.style.setProperty('--color-panel-bg', surface.panel);
            root.style.setProperty('--color-player-bg', surface.player);
            root.style.setProperty('--color-bg-secondary', surface.panel.startsWith('#') ? surface.panel : '#1e1e1e');
        } else if (PANEL_TEXTURE) {
            root.style.setProperty('--color-panel-bg', PANEL_TEXTURE);
            root.style.setProperty('--color-player-bg', PANEL_TEXTURE);
            root.style.removeProperty('--color-bg-secondary');
        }
        if (surface.border) root.style.setProperty('--color-border', surface.border);
        else root.style.removeProperty('--color-border');
    }
    if (accent) {
        root.style.setProperty('--color-accent', accent.accent);
        root.style.setProperty('--color-accent-hover', accent.hover);
        root.style.setProperty('--color-accent-light', accent.light);
        root.style.setProperty('--color-accent-bright', accent.bright);
        root.style.setProperty('--color-loop', accent.accent);
        root.style.setProperty('--color-loop-hover', accent.hover);
    } else {
        ['--color-accent','--color-accent-hover','--color-accent-light','--color-accent-bright','--color-loop','--color-loop-hover']
            .forEach((v) => root.style.removeProperty(v));
    }
    if (!silent) {
        try { localStorage.setItem(DESIGN_LAB_KEY, JSON.stringify(lab)); } catch (e) { /* ignore */ }
    }
}
